/**
 * @file        src/layout/ProtectedRoute.jsx
 * @description Bounces unauthenticated requests to /login while preserving
 *              the original destination. Optional `role` prop (a string, or an
 *              array to accept several) bounces a logged-in user of the WRONG
 *              role back to the dashboard — used to guard /platform/* to
 *              super_admin only, and the tenant-operations routes to
 *              tenant_admin + staff. This is a UX guard; the real security
 *              boundary is the backend's authorize()/requireModule() on those
 *              API routes — which module a staff account can actually USE
 *              inside the tenant-operations area is enforced there (and hidden
 *              from Sidebar.jsx), not here.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-27 Claude — Added optional `role` prop for the Platform routes.
 *   - 2026-08-04 Claude — `role` accepts an array — the tenant-operations
 *                          wrapper now needs BOTH tenant_admin and staff to
 *                          pass, and a single string wouldn't say that.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth.store';

export default function ProtectedRoute({ children, role }) {
  const status = useAuth((s) => s.status);
  const user = useAuth((s) => s.user);
  const loc = useLocation();

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="login-shell">
        <div className="glass login-card">
          <div className="skeleton sk-row" />
          <div className="skeleton sk-row" />
          <div className="skeleton sk-row" />
        </div>
      </div>
    );
  }
  if (status === 'guest') return <Navigate to="/login" state={{ from: loc }} replace />;
  const allowedRoles = Array.isArray(role) ? role : role ? [role] : null;
  if (allowedRoles && !allowedRoles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
}
